import React, { useState, useMemo, useEffect } from "react";
import { getAppointmentOwnerName } from "../../utils/appointmentOwner";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import AppointmentBadges from "../ActionReportBadge/AppointmentBadges";
import IconsLegend from "../IconsLegend/IconsLegend";
import i18n from "../../../../i18n";
import styles from "../../appointmentsPage.module.css";
import {
  IAppointment,
  IAppointmentStats,
  IWeekDay,
} from "../../types/appointments.types";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import AppointmentStats from "../AppointmentStats/AppointmentStats";
import AppointmentDetailsModal from "../AppointmentDetailsModal/AppointmentDetailsModal";
import CreateAppointmentModal from "../CreateAppointmentModal/CreateAppointmentModal";
import MoveConfirmationModal from "../MoveConfirmationModal/MoveConfirmationModal";
import {
  getAppointmentColors,
  getFundingSourceColor,
  truncateText,
} from "../../utils/fundingSourceColors";
import {
  getInitialDateForView,
  saveDateForView,
} from "../../utils/calendarStorage";
import { usePatchAppointmentMutation } from "../../../../services/api/utils.api";
import {
  calculateVerticalOffset,
  calculateHeight,
  detectOverlaps,
  calculateHorizontalLayout,
  getContainingHourSlot,
  timeToMinutes,
  calculateEndTime,
  AppointmentPosition,
} from "../../utils/timeUtils";
import ExportTableButtons from "../../../../components/exportTableButtons/ExportTableButtons";
import {
  buildAppointmentsExport,
  buildAdminFilterSummary,
} from "../../utils/buildAppointmentsExport";
import { useCalendarColumns } from "../../hooks/useCalendarColumns";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

/**
 * Weekly View Component
 *
 * Displays appointments in a weekly calendar grid with drag-and-drop support.
 * Uses real API data with smart month-based caching.
 */
const WeeklyView: React.FC = () => {
  const [weekStart, setWeekStart] = useState(() =>
    getInitialDateForView("weekly"),
  );
  const [selectedAppointment, setSelectedAppointment] =
    useState<IAppointment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Create Appointment State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDate, setCreateModalDate] = useState("");
  const [createModalTime, setCreateModalTime] = useState("");
  const [createModalTherapistId, setCreateModalTherapistId] = useState<
    number | undefined
  >(undefined);

  // Drag & Drop State
  const [draggedAppointment, setDraggedAppointment] =
    useState<IAppointment | null>(null);
  const [draggedFromSlot, setDraggedFromSlot] = useState<{
    day: IWeekDay;
    time: string;
  } | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{
    date: string;
    time: string;
    therapistId?: number;
  } | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<
    "valid" | "invalid" | "warning"
  >("valid");
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [pendingMove, setPendingMove] = useState<{
    appointment: IAppointment;
    fromDay: IWeekDay;
    fromTime: string;
    fromEndTime: string;
    toDay: IWeekDay;
    toTime: string;
    toEndTime: string;
    toTherapistId: number;
    toTherapistGuid?: string;
    toTherapistName: string;
    warning?: string;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  // Time Slot Modal State
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    date: string;
    time: string;
    appointments: IAppointment[];
  } | null>(null);

  // Expandable Hours State
  const [expandedHours, setExpandedHours] = useState<Record<string, boolean>>(
    {},
  );
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  const admin = useAppSelector(adminSelector);

  // API hooks
  const [patchAppointment] = usePatchAppointmentMutation();

  // Use appointments data hook
  // Use shared appointments context (data is cached and shared across all views)
  const {
    ensureDateRangeLoaded,
    reloadMonth,
    filterAppointments: filterAppointmentsHook,
    therapistsList,
    therapistAvailability,
    companiesOptions,
    fundingSourcesList,
    filters,
    isFullscreen,
    showFiltersInFullscreen,
  } = useAppointmentsContext();

  // Load week range on mount and when week changes
  useEffect(() => {
    if (admin.companyId) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      ensureDateRangeLoaded(weekStart, weekEnd);
    }
  }, [admin.companyId, weekStart, ensureDateRangeLoaded]);

  // Persist date when it changes (for navigation back)
  useEffect(() => {
    saveDateForView("weekly", weekStart);
  }, [weekStart]);

  // Get week range for filtering
  const weekEnd = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [weekStart]);

  // Filter appointments for the week
  const rangeAppointments = useMemo(() => {
    return filterAppointmentsHook({
      dateRange: { start: weekStart, end: weekEnd },
      fakeCompanyIds: filters.fakeCompanyIds,
      therapistIds: filters.therapistIds,
      fundingSourceIds: filters.fundingSourceIds,
      attendanceStatuses: filters.attendanceStatuses,
      patientSearch: filters.patientSearch,
      includeOtherEventsForAdminId: Number(admin.id),
    });
  }, [
    filterAppointmentsHook,
    weekStart,
    weekEnd,
    filters.fakeCompanyIds,
    filters.therapistIds,
    filters.fundingSourceIds,
    filters.attendanceStatuses,
    filters.patientSearch,
    admin.id,
  ]);

  // Column axis, shared across the daily, weekly and monthly boards. Weekly behaves like daily: every machine gets a
  // column until specific devices are picked.
  const { columns: displayedTherapists, isColumnSplit, isDeviceMode, matchesColumn, matchesAxis } =
    useCalendarColumns({
      appointments: rangeAppointments,
      filters,
      splitDevicesWhenNoSelection: true,
    });

  // Narrowed to the active axis here, at the source, so stats, layouts, slots and the export all
  // inherit it. In therapist mode filterAppointments already applied the filter upstream; device
  // mode has no equivalent there, which is why it has to happen at this point.
  const appointments = useMemo(
    () => rangeAppointments.filter(matchesAxis),
    [rangeAppointments, matchesAxis],
  );

  // Calculate stats
  const stats: IAppointmentStats = useMemo(() => {
    const total = appointments.length;
    const activeTherapists = new Set(appointments.map((a) => a.therapistsId))
      .size;

    return {
      totalToday: 0,
      totalWeek: total,
      confirmed: total,
      pending: 0,
      cancelled: 0,
      activeTherapists,
      totalTherapists: therapistsList.filter((t) => t.isActive).length,
    };
  }, [appointments, therapistsList]);

  // Generate hourly time slots from 07:00 to 22:00
  const hourlySlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = 7; hour <= 22; hour++) {
      slots.push(`${hour.toString().padStart(2, "0")}:00`);
    }
    return slots;
  }, []);

  // Get 15-minute sub-slots for a specific hour (e.g., "08" => ["08:15", "08:30", "08:45"])
  const getSubSlots = (hour: string): string[] => {
    return ["15", "30", "45"].map((minute) => `${hour}:${minute}`);
  };

  // Toggle hour expansion
  const toggleHour = (hour: string) => {
    setExpandedHours((prev) => ({
      ...prev,
      [hour]: !prev[hour],
    }));
  };

  // Toggle all hours at once
  const toggleAllHours = () => {
    const allExpanded = hourlySlots.every((slot) => {
      const [hourStr] = slot.split(":");
      return expandedHours[hourStr];
    });

    if (allExpanded) {
      // Collapse all
      setExpandedHours({});
    } else {
      // Expand all
      const newExpanded: Record<string, boolean> = {};
      hourlySlots.forEach((slot) => {
        const [hourStr] = slot.split(":");
        newExpanded[hourStr] = true;
      });
      setExpandedHours(newExpanded);
    }
  };

  const dayNames = [
    t("APPOINTMENTS_DAY_SUNDAY"),
    t("APPOINTMENTS_DAY_MONDAY"),
    t("APPOINTMENTS_DAY_TUESDAY"),
    t("APPOINTMENTS_DAY_WEDNESDAY"),
    t("APPOINTMENTS_DAY_THURSDAY"),
    t("APPOINTMENTS_DAY_FRIDAY"),
    t("APPOINTMENTS_DAY_SATURDAY"),
  ];

  const weekDays: IWeekDay[] = useMemo(() => {
    const formatDate = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const todayStr = formatDate(new Date());

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const dateStr = formatDate(date);
      return {
        date: dateStr,
        dayName: dayNames[i],
        dayNumber: date.getDate(),
        isToday: dateStr === todayStr,
        isWeekend: i === 5 || i === 6,
      };
    });
  }, [weekStart]);

  // Column axis, shared with the daily and monthly boards. Weekly behaves like daily: every
  // machine gets a column until specific devices are picked.

  // Calculate appointment layouts (positions for overlapping appointments)
  // Only used when therapist filter is active
  const appointmentLayouts = useMemo(() => {
    if (!isColumnSplit) return new Map();

    const layouts = new Map<
      number,
      {
        position: AppointmentPosition;
        verticalOffset: number;
        height: number;
        containingSlot: string;
      }
    >();

    // Iterate through each displayed therapist
    displayedTherapists.forEach((therapist) => {
      const isVirtualCalendar = therapist.id === -1;
      // Iterate through each day
      weekDays.forEach((day) => {
        // Get appointments for this specific therapist and day
        const dayTherapistAppointments = appointments.filter((apt) => {
          if (apt.date !== day.date) return false;
          if (isVirtualCalendar) {
            const isGoogleEvent = apt.source === "google_calendar";
            const isOther =
              (!apt.patientName && !apt.treatmentGroupName && !apt.mediaId) ||
              isGoogleEvent;
            return isOther && apt.therapistsId === Number(admin.id);
          }
          return matchesColumn(apt, therapist.id);
        });

        const overlapGroups = detectOverlaps(dayTherapistAppointments);

        overlapGroups.forEach((group) => {
          const positions = calculateHorizontalLayout(group);

          positions.forEach((pos) => {
            const appointment = dayTherapistAppointments.find(
              (apt) => apt.id === pos.appointmentId,
            );
            if (!appointment) return;

            const startTime = appointment.time || "07:00";
            const containingSlot = getContainingHourSlot(startTime);
            const [hourStr] = containingSlot.split(":");
            const isExpanded = expandedHours[hourStr] || false;

            // Determine the specific sub-slot if expanded
            let effectiveContainingSlot = containingSlot;
            const originalHour = parseInt(startTime.split(":")[0], 10);

            // Only split into sub-slots if the appointment actually starts in or after 07:00
            // Appointments before 07:00 stay in the main 07:00 slot even when expanded
            if (isExpanded && originalHour >= 7 && originalHour < 23) {
              const [_, minuteStr] = startTime.split(":");
              const minutes = parseInt(minuteStr, 10);
              if (minutes >= 45) {
                effectiveContainingSlot = `${hourStr}:45`;
              } else if (minutes >= 30) {
                effectiveContainingSlot = `${hourStr}:30`;
              } else if (minutes >= 15) {
                effectiveContainingSlot = `${hourStr}:15`;
              }
            }

            // Calculate offset relative to the specific slot (main or sub-slot)
            const verticalOffset = calculateVerticalOffset(
              startTime,
              effectiveContainingSlot,
              isExpanded,
            );
            const height = calculateHeight(
              startTime,
              appointment.duration || 45,
              expandedHours,
            );

            layouts.set(pos.appointmentId, {
              position: pos,
              verticalOffset,
              height,
              containingSlot: effectiveContainingSlot,
            });
          });
        });
      });
    });

    return layouts;
  }, [
    appointments,
    weekDays,
    displayedTherapists,
    expandedHours,
    isColumnSplit,
  ]);

  const getAppointmentsForSlot = (
    date: string,
    time: string,
    therapistId?: number,
  ): IAppointment[] => {
    const [slotHourStr, slotMinuteStr] = time.split(":");
    const slotHour = parseInt(slotHourStr, 10);
    const slotMinute = parseInt(slotMinuteStr, 10);

    // Check if this hour is expanded
    const isExpanded = expandedHours[slotHourStr];

    return appointments.filter((apt: IAppointment) => {
      // Filter by date
      if (apt.date !== date) return false;

      // Filter by column if provided. This is the render path's own matcher — matchesColumn keeps
      // it on whichever axis is active, so a device column matches the booked machine rather than
      // the therapist. Comparing therapistsId here only ever matched an appointment whose therapist
      // happened to BE the device.
      if (therapistId !== undefined) {
        if (therapistId === -1) {
          const isGoogleEvent = apt.source === "google_calendar";
          const isOther =
            (!apt.patientName && !apt.treatmentGroupName && !apt.mediaId) ||
            isGoogleEvent;
          if (!(isOther && apt.therapistsId === Number(admin.id))) return false;
        } else if (!matchesColumn(apt, therapistId)) {
          return false;
        }
      }

      const aptTime = apt.time;
      if (!aptTime) {
        return slotHour === 7 && slotMinute === 0;
      }

      const [aptHourStr, aptMinuteStr] = aptTime.split(":");
      const aptHour = parseInt(aptHourStr, 10);
      const aptMinute = parseInt(aptMinuteStr || "0", 10);

      // Special handling for appointments before 07:00
      if (aptHour < 7) {
        // Show in 07:00 slot when collapsed or in 07:00 slot when expanded
        return slotHour === 7 && slotMinute === 0;
      }

      // Special handling for appointments after 22:00
      if (aptHour >= 23) {
        // For proportional view, put everything in the 22:00 main slot
        if (isColumnSplit) {
          return slotHour === 22 && slotMinute === 0;
        }

        // Show in 22:00 slot when collapsed or in 22:45 slot when expanded
        if (isExpanded && expandedHours["22"]) {
          return slotHour === 22 && slotMinute === 45;
        } else {
          return slotHour === 22 && slotMinute === 0;
        }
      }

      // If this is a main hour slot (xx:00)
      if (slotMinute === 0) {
        // For proportional view, main slot handles ALL appointments for that hour
        if (isColumnSplit) {
          return aptHour === slotHour;
        }

        if (isExpanded) {
          // When expanded, main slot shows only 00-14 minute appointments
          return aptHour === slotHour && aptMinute < 15;
        } else {
          // When collapsed, main slot shows ALL appointments in that hour
          return aptHour === slotHour;
        }
      } else {
        // This is a sub-slot (xx:15, xx:30, xx:45)

        // For proportional view, we return all appointments for the hour.
        // The rendering loop will filter them based on their calculated 'containingSlot'.
        if (isColumnSplit) {
          return aptHour === slotHour;
        }

        // Match appointment to the 15-minute slot it falls into
        const aptTotalMinutes = aptHour * 60 + aptMinute;
        const slotTotalMinutes = slotHour * 60 + slotMinute;
        const aptSlotMinutes = Math.floor(aptTotalMinutes / 15) * 15;
        return aptSlotMinutes === slotTotalMinutes;
      }
    });
  };

  const isSlotAvailable = (
    date: string,
    time: string,
    therapistId?: number,
  ) => {
    if (!therapistId || therapistId === -1) return false;

    const slotDate = new Date(date);
    // getDay() returns 0 for Sunday, DB uses 1 for Sunday
    const dayId = slotDate.getDay() + 1;

    return therapistAvailability.some((avail) => {
      // Backend handles PascalCase or camelCase; often therapistsId or TherapistsId
      const availTherapistId =
        avail.therapistsId || (avail as any).TherapistsId;
      if (availTherapistId !== therapistId) return false;

      const availDayId = avail.dayId || (avail as any).DayId;
      if (availDayId !== dayId) return false;

      const availStatusId = avail.statusId ?? (avail as any).StatusId;
      const isAvailable = avail.isAvailable ?? (avail as any).IsAvailable;
      if (!isAvailable || availStatusId !== 1) return false;

      const slotTimeMin = timeToMinutes(time);
      const availStart = avail.startTime || (avail as any).StartTime;
      const availEnd = avail.endTime || (avail as any).EndTime;
      const availStartMin = timeToMinutes(availStart);
      const availEndMin = timeToMinutes(availEnd);

      return slotTimeMin >= availStartMin && slotTimeMin < availEndMin;
    });
  };

  const getTherapistHours = (date: string, therapistId: number) => {
    if (!therapistId || therapistId === -1) return [];

    const slotDate = new Date(date);
    const dayId = slotDate.getDay() + 1;

    const hours = therapistAvailability
      .filter((avail) => {
        const availTherapistId =
          avail.therapistsId || (avail as any).TherapistsId;
        if (availTherapistId !== therapistId) return false;

        const availDayId = avail.dayId || (avail as any).DayId;
        if (availDayId !== dayId) return false;

        const availStatusId = avail.statusId ?? (avail as any).StatusId;
        const isAvailable = avail.isAvailable ?? (avail as any).IsAvailable;
        if (!isAvailable || availStatusId !== 1) return false;
        return true;
      })
      .map((avail) => {
        const availStart = avail.startTime || (avail as any).StartTime;
        const availEnd = avail.endTime || (avail as any).EndTime;
        if (!availStart || !availEnd) return null;
        return `${availStart.substring(0, 5)} - ${availEnd.substring(0, 5)}`;
      })
      .filter(Boolean) as string[];

    return Array.from(new Set(hours)).sort();
  };

  // Removed insurance-based styling since insuranceType field was removed

  const handleAppointmentClick = (appointment: IAppointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleAppointmentCancelled = () => {
    reloadMonth(weekStart);
  };

  const handleOpenCreateModal = (
    date: string,
    time: string,
    therapistId?: number,
  ) => {
    setCreateModalDate(date);
    setCreateModalTime(time);
    setCreateModalTherapistId(therapistId);
    setShowCreateModal(true);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    reloadMonth(weekStart);
    showToastMessage(
      t("MEETING_CREATED_SUCCESSFULLY") || "Appointment created successfully",
      "success",
    );
  };

  const handleWeekChange = (direction: "prev" | "next") => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + (direction === "next" ? 7 : -7));
    setWeekStart(newStart);
    // Ensure new week range is loaded
    const newEnd = new Date(newStart);
    newEnd.setDate(newEnd.getDate() + 6);
    ensureDateRangeLoaded(newStart, newEnd);
  };

  const formatWeekRange = (): string => {
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 6);

    const startMonth = weekStart.getMonth();
    const endMonth = endDate.getMonth();
    const startYear = weekStart.getFullYear();
    const endYear = endDate.getFullYear();

    const locale = getLocale();

    // Format: start date first, then end date
    // If same month and year: "23 - 29 November 2025" or "23 - 29 נובמבר 2025"
    if (startMonth === endMonth && startYear === endYear) {
      const monthYear = weekStart.toLocaleDateString(locale, {
        month: "long",
        year: "numeric",
      });
      return `${weekStart.getDate()} - ${endDate.getDate()} ${monthYear}`;
    }

    // If different months but same year: "30 November - 6 December 2025"
    if (startYear === endYear) {
      const startMonthName = weekStart.toLocaleDateString(locale, {
        month: "long",
      });
      const endMonthName = endDate.toLocaleDateString(locale, {
        month: "long",
      });
      return `${weekStart.getDate()} ${startMonthName} - ${endDate.getDate()} ${endMonthName} ${endYear}`;
    }

    // If different years: "28 December 2025 - 3 January 2026"
    const startFull = weekStart.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const endFull = endDate.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return `${startFull} - ${endFull}`;
  };

  // ================================
  // DRAG & DROP HANDLERS
  // ================================

  const getDayByDate = (date: string): IWeekDay | undefined => {
    return weekDays.find((day) => day.date === date);
  };

  // Replaced by calculateEndTime from utils
  // const calculateEndTime = (startTime: string, duration: number): string => { ... }

  const validateDrop = (
    targetDate: string,
    targetTime: string,
  ): { status: "valid" | "invalid" | "warning"; message?: string } => {
    // Always allow drop - validation will happen in the modal
    return { status: "valid" };
  };

  // Drag & Drop handlers - disabled for now, keeping for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragStart = (
    e: React.DragEvent,
    appointment: IAppointment,
    day: IWeekDay,
    time: string,
  ) => {
    setDraggedAppointment(appointment);
    setDraggedFromSlot({ day, time });

    // Set drag image (optional - browser default works fine)
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", appointment.id.toString());
    }

    // Store reference to element before the async callback
    const target = e.currentTarget as HTMLElement;

    // Add dragging class after a small delay (allows drag image to capture)
    setTimeout(() => {
      if (target) {
        target.classList.add(styles.dragging);
      }
    }, 0);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragEnd = (e: React.DragEvent) => {
    const el = e.currentTarget as HTMLElement;
    if (el) {
      el.classList.remove(styles.dragging);
    }

    setDragOverSlot(null);
    setDragOverStatus("valid");
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragOver = (
    e: React.DragEvent,
    date: string,
    time: string,
    therapistId?: number,
  ) => {
    e.preventDefault();

    if (!draggedAppointment || !draggedFromSlot) return;

    // Don't show drop indicator on the same slot
    if (
      date === draggedFromSlot.day.date &&
      time === draggedFromSlot.time &&
      (therapistId === undefined ||
        therapistId === draggedAppointment.therapistsId)
    ) {
      setDragOverSlot(null);
      return;
    }

    const validation = validateDrop(date, time);
    setDragOverSlot({ date, time, therapistId });
    setDragOverStatus(validation.status);

    e.dataTransfer.dropEffect =
      validation.status === "invalid" ? "none" : "move";
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the slot entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDragOverSlot(null);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDrop = (
    e: React.DragEvent,
    targetDate: string,
    targetTime: string,
    therapistId?: number,
  ) => {
    e.preventDefault();

    if (!draggedAppointment || !draggedFromSlot) return;

    const targetDay = getDayByDate(targetDate);
    if (!targetDay) return;

    // Don't do anything if dropping on the same slot (same day, time AND therapist)
    if (
      targetDate === draggedFromSlot.day.date &&
      targetTime === draggedFromSlot.time &&
      (!therapistId || therapistId === draggedAppointment.therapistsId)
    ) {
      setDraggedAppointment(null);
      setDraggedFromSlot(null);
      setDragOverSlot(null);
      return;
    }

    const validation = validateDrop(targetDate, targetTime);

    if (validation.status === "invalid") {
      showToastMessage(
        validation.message || t("APPOINTMENTS_CANNOT_MOVE"),
        "error",
      );
      setDraggedAppointment(null);
      setDraggedFromSlot(null);
      setDragOverSlot(null);
      return;
    }

    // Calculate end times
    const duration = draggedAppointment.duration || 30;
    const fromEndTime = calculateEndTime(draggedFromSlot.time, duration);
    const toEndTime = calculateEndTime(targetTime, duration);

    // Show confirmation modal
    const resolvedToTherapistId =
      therapistId || draggedAppointment.therapistsId || 0;
    const targetTherapist = therapistsList.find(
      (t) => t.id === resolvedToTherapistId,
    );

    setPendingMove({
      appointment: draggedAppointment,
      fromDay: draggedFromSlot.day, // Assuming fromDayForModal should be draggedFromSlot.day
      fromTime: draggedAppointment.time,
      fromEndTime: fromEndTime, // Assuming endTime should be fromEndTime
      toDay: targetDay, // Assuming toDayForModal should be targetDay
      toTime: targetTime,
      toEndTime: calculateEndTime(targetTime, duration),
      toTherapistId: resolvedToTherapistId,
      toTherapistGuid: targetTherapist?.guid,
      toTherapistName:
        targetTherapist?.name || draggedAppointment.therapistName,
      warning: validation.status === "warning" ? validation.message : undefined,
    });
    setShowMoveConfirmation(true);
    setDragOverSlot(null);
  };

  const handleConfirmMove = async (
    newDate: string,
    newTime: string,
    newEndTime: string,
    newTherapistId: number,
  ) => {
    if (!pendingMove) return;

    try {
      const therapist = therapistsList.find((t) => t.id === newTherapistId);

      // Update appointment via API
      await patchAppointment({
        id: pendingMove.appointment.id,
        appointmentDate:
          newDate !== pendingMove.appointment.date ? newDate : undefined,
        startTime:
          newTime !== pendingMove.appointment.time ? newTime : undefined,
        endTime:
          newEndTime !== pendingMove.appointment.endTime
            ? newEndTime
            : undefined,
        therapistsGuid:
          therapist?.guid !== pendingMove.appointment.therapistsGuid
            ? therapist?.guid
            : undefined,
        therapistsId:
          newTherapistId !== pendingMove.appointment.therapistsId
            ? newTherapistId
            : undefined,
      }).unwrap();

      // Show success animation
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 600);

      // Show toast
      showToastMessage(
        t("APPOINTMENTS_MOVED_SUCCESS") || "Appointment moved successfully",
        "success",
      );

      // Cleanup
      setShowMoveConfirmation(false);
      setPendingMove(null);
      setDraggedAppointment(null);
      setDraggedFromSlot(null);

      // Reload the week to get updated data
      reloadMonth(weekStart);
    } catch (error) {
      console.error("Failed to move appointment:", error);
      showToastMessage(
        t("APPOINTMENTS_MOVE_FAILED") || "Failed to move appointment",
        "error",
      );
    }
  };

  const handleCancelMove = () => {
    setShowMoveConfirmation(false);
    setPendingMove(null);
    setDraggedAppointment(null);
    setDraggedFromSlot(null);
  };

  const showToastMessage = (message: string, type: "success" | "error") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const getSlotClassName = (
    date: string,
    time: string,
    therapistId?: number,
  ): string => {
    let className = styles.timeSlot;

    if (
      dragOverSlot &&
      dragOverSlot.date === date &&
      dragOverSlot.time === time &&
      (therapistId === undefined || dragOverSlot.therapistId === therapistId)
    ) {
      className += " " + styles.dragOver;
      if (dragOverStatus === "invalid") {
        className += " " + styles.dragOverInvalid;
      } else if (dragOverStatus === "warning") {
        className += " " + styles.dragOverWarning;
      }
    }

    return className;
  };

  const getAppointmentTooltip = (apt: IAppointment): string => {
    const isGoogleEvent = apt.source === "google_calendar";
    const isOther = !apt.usersId && !apt.treatmentGroupId && !isGoogleEvent;

    let tooltip = "";

    // Time
    if (apt.time) {
      tooltip += `${apt.time}`;
      if (apt.endTime) {
        tooltip += ` - ${apt.endTime}`;
      } else if (apt.duration) {
        const endTime = calculateEndTime(apt.time, apt.duration);
        tooltip += ` - ${endTime}`;
      }
      tooltip += " | ";
    }

    // Title/Patient
    if (isGoogleEvent) {
      tooltip += `📅 ${apt.summary || apt.description || "Google Calendar"}`;
    } else if (isOther) {
      tooltip += `📝 ${apt.comments || t("OTHER") || "אחר"}`;
    } else if (apt.treatmentGroupName && !apt.patientName) {
      tooltip += `${apt.treatmentGroupName}`;
    } else {
      tooltip += `${apt.patientName || ""}`;
    }

    // Therapist
    if (!isGoogleEvent && !isOther && getAppointmentOwnerName(apt)) {
      tooltip += ` | ${getAppointmentOwnerName(apt)}`;
    }

    return tooltip;
  };

  const showStats = !isFullscreen || showFiltersInFullscreen;

  // Export payload (PDF/Excel) for the currently displayed, filtered week.
  const exportProps = useMemo(
    () =>
      buildAppointmentsExport(appointments, {
        viewType: "weekly",
        rangeLabel: formatWeekRange(),
        filterSummary: buildAdminFilterSummary(filters, {
          therapistsList,
          companiesOptions,
          fundingSourcesList,
        }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appointments,
      weekStart,
      filters,
      therapistsList,
      companiesOptions,
      fundingSourcesList,
    ],
  );

  return (
    <div>
      {/* Statistics - hidden in fullscreen when filters are collapsed */}
      {showStats && <AppointmentStats stats={stats} viewType="weekly" />}

      {/* Calendar Section */}
      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <div className={styles.calendarHeaderLeft}>
            <h2 className={styles.calendarTitle}>{formatWeekRange()}</h2>
            <div className={styles.dragHint}>
              <span className={styles.dragHintIcon}>💡</span>
              <span>{t("APPOINTMENTS_DRAG_HINT")}</span>
            </div>
            <IconsLegend />
          </div>
          <div className={styles.calendarNav}>
            <button
              className={styles.navBtn}
              onClick={() => handleWeekChange("next")}
              aria-label="Next"
            >
              ‹
            </button>
            <button
              className={styles.todayBtn}
              onClick={() => {
                const today = new Date();
                const day = today.getDay();
                const diff = today.getDate() - day;
                setWeekStart(new Date(today.setDate(diff)));
              }}
            >
              {t("APPOINTMENTS_TODAY")}
            </button>
            <span className={styles.currentDate}>
              {weekStart.toLocaleDateString(getLocale(), {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              className={styles.navBtn}
              onClick={() => handleWeekChange("prev")}
              aria-label="Previous"
            >
              ›
            </button>
            <ExportTableButtons
              disabled={appointments.length === 0}
              {...exportProps}
            />
          </div>
        </div>

        {/* Weekly Calendar Grid - Desktop */}
        <div className={styles.weeklyGridDesktop}>
          <div className={styles.dailyScrollContainer}>
            {displayedTherapists.length === 0 ? (
              <div
                className={styles.emptyState}
                style={{ padding: "40px", textAlign: "center" }}
              >
                <p className={styles.emptyText}>
                  {t("APPOINTMENTS_NO_APPOINTMENTS_FOR_DAY") ||
                    "No therapists available"}
                </p>
              </div>
            ) : (
              <div
                className={`${styles.weeklyCalendar} ${styles.dailyTherapistGrid}`}
                style={{
                  gridTemplateColumns: isColumnSplit
                    ? `auto repeat(${weekDays.length * displayedTherapists.length}, minmax(180px, 1fr))`
                    : `auto repeat(${weekDays.length}, minmax(0, 1fr))`,
                }}
              >
                {/* Header Row - Days */}
                <div
                  className={`${styles.dailyTimeHeaderCorner} ${styles.stickyCorner}`}
                >
                  <button
                    className={styles.toggleAllBtn}
                    onClick={toggleAllHours}
                  >
                    {hourlySlots.every((slot) => {
                      const [hourStr] = slot.split(":");
                      return expandedHours[hourStr];
                    }) ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="18 15 12 9 6 15"></polyline>
                        <polyline points="18 21 12 15 6 21"></polyline>
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                        <polyline points="6 3 12 9 18 3"></polyline>
                      </svg>
                    )}
                  </button>
                </div>
                {weekDays.map((day, index) => (
                  <div
                    key={day.date}
                    className={`${styles.dayHeader} ${
                      day.isToday ? styles.dayHeaderToday : ""
                    } ${styles.dayDivider}`}
                    style={{
                      gridColumn: `span ${isColumnSplit ? displayedTherapists.length : 1}`,
                    }}
                  >
                    <span className={styles.dayName}>{day.dayName}</span>
                    <span className={styles.dayDate}>{day.dayNumber}</span>
                  </div>
                ))}

                {/* Sub-Header Row - Therapists (Only if filter is active) */}
                {isColumnSplit && (
                  <>
                    <div
                      className={`${styles.dailyTimeHeader} ${styles.stickyTherapistSpacer}`}
                      style={{
                        background: "#f9fafb",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    ></div>
                    {weekDays.map((day) =>
                      displayedTherapists.map((therapist, tIndex) => (
                        <div
                          key={`${day.date}-${therapist.id}`}
                          className={`${styles.dailyTherapistHeader} ${
                            tIndex === displayedTherapists.length - 1
                              ? styles.lastTherapistHeader
                              : ""
                          }`}
                        >
                          {therapist.name}
                          {therapist.id !== -1 && (
                            <div className={styles.therapistHoursContainer}>
                              {getTherapistHours(day.date, therapist.id).map(
                                (hourStr, idx) => (
                                  <span
                                    key={idx}
                                    className={styles.therapistHourBadge}
                                  >
                                    {hourStr}
                                  </span>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      )),
                    )}
                  </>
                )}

                {/* Time Slots */}
                {hourlySlots.map((hourSlot) => {
                  const [hourStr] = hourSlot.split(":");
                  const isExpanded = expandedHours[hourStr];
                  // Only show subslots if filter is active AND hour is expanded
                  const subSlots = isExpanded ? getSubSlots(hourStr) : [];

                  return (
                    <React.Fragment key={hourSlot}>
                      {/* Main Hour Row */}
                      <div
                        className={`${styles.dailyTimeHeader} ${styles.timeHeaderHour}`}
                      >
                        <button
                          onClick={() => toggleHour(hourStr)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            marginInlineEnd: "4px",
                            fontSize: "12px",
                          }}
                          title={isExpanded ? "Collapse" : "Expand"}
                        >
                          {isExpanded ? "▲" : "▼"}
                        </button>
                        {hourSlot}
                      </div>
                      {/* Slots for each day/therapist */}
                      {weekDays.map((day) => {
                        if (isColumnSplit) {
                          return displayedTherapists.map(
                            (therapist, tIndex) => {
                              const slotAppointments = getAppointmentsForSlot(
                                day.date,
                                hourSlot,
                                therapist.id,
                              );

                              // Filter appointments for this slot based on layouts if using proportional view
                              const displayedAppointments =
                                isColumnSplit
                                  ? slotAppointments.filter((apt) => {
                                      const layout = appointmentLayouts.get(
                                        apt.id,
                                      );
                                      return (
                                        layout &&
                                        layout.containingSlot === hourSlot
                                      );
                                    })
                                  : slotAppointments.slice(0, 3);

                              const hasMore =
                                !isColumnSplit &&
                                slotAppointments.length > 3;

                              const isVirtual = therapist.id === -1;
                              const slotIsAvailable =
                                !isVirtual &&
                                isSlotAvailable(
                                  day.date,
                                  hourSlot,
                                  therapist.id,
                                );

                              return (
                                <div
                                  key={`${day.date}-${therapist.id}-${hourSlot}`}
                                  className={`${getSlotClassName(day.date, hourSlot, therapist.id)} ${
                                    tIndex === displayedTherapists.length - 1
                                      ? styles.dayDivider
                                      : ""
                                  }`}
                                  onDragOver={
                                    isVirtual
                                      ? undefined
                                      : (e) =>
                                          handleDragOver(
                                            e,
                                            day.date,
                                            hourSlot,
                                            therapist.id,
                                          )
                                  }
                                  onDragLeave={
                                    isVirtual ? undefined : handleDragLeave
                                  }
                                  onDrop={
                                    isVirtual
                                      ? undefined
                                      : (e) =>
                                          handleDrop(
                                            e,
                                            day.date,
                                            hourSlot,
                                            therapist.id,
                                          )
                                  }
                                  onClick={(e) => {
                                    if (e.target === e.currentTarget) {
                                      // Keep slot clickable if you want, but user specifically asked for the plus btn
                                    }
                                  }}
                                  style={{
                                    position: "relative",
                                    height: "70px",
                                    overflow: "visible",
                                    ...(slotIsAvailable
                                      ? {
                                          backgroundColor:
                                            "rgba(107, 70, 193, 0.1)",
                                        }
                                      : {}),
                                  }}
                                >
                                  <button
                                    className={styles.addAppointmentBtn}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenCreateModal(
                                        day.date,
                                        hourSlot,
                                        isVirtual ? undefined : therapist.id,
                                      );
                                    }}
                                  >
                                    +
                                  </button>
                                  {displayedAppointments.map(
                                    (apt: IAppointment) => {
                                      const fundingColor =
                                        getAppointmentColors(apt);
                                      const isGoogleEvent =
                                        apt.source === "google_calendar";

                                      // Calculate styles based on view mode
                                      let appStyles: React.CSSProperties = {
                                        borderRightColor:
                                          fundingColor.borderColor,
                                        backgroundColor:
                                          fundingColor.backgroundColor,
                                      };

                                      if (isColumnSplit) {
                                        const layout = appointmentLayouts.get(
                                          apt.id,
                                        );
                                        if (
                                          layout &&
                                          layout.containingSlot === hourSlot
                                        ) {
                                          appStyles = {
                                            ...appStyles,
                                            position: "absolute",
                                            top: `${layout.verticalOffset}px`,
                                            height: `${layout.height}px`,
                                            zIndex: 10,
                                          };

                                          if (
                                            layout.position.totalColumns > 1
                                          ) {
                                            const widthPercent =
                                              98 / layout.position.totalColumns;
                                            const leftPercent =
                                              layout.position.columnIndex *
                                                widthPercent +
                                              1;
                                            appStyles.width = `calc(${widthPercent}% - 4px)`;
                                            // Handle RTL/LTR logic loosely or enforce standard left for positioning
                                            // Since we are using LTR for positioning logic usually:
                                            appStyles.left = `calc(${leftPercent}% + 2px)`; // Add slight offset to center within slot
                                            appStyles.right = "auto"; // Override any RTL default
                                          } else {
                                            appStyles.left = "2px";
                                            appStyles.right = "2px";
                                            appStyles.width =
                                              "calc(100% - 4px)"; // Ensure consistent spacing
                                          }
                                        } else if (!layout) {
                                          // Fallback
                                          appStyles.position = "relative";
                                        } else {
                                          // Should have been filtered out
                                          return null;
                                        }
                                      }

                                      return (
                                        <div
                                          key={apt.id}
                                          className={`${styles.weeklyAppointment} ${isColumnSplit ? styles.weeklyAppointmentProportional : ""}`}
                                          style={appStyles}
                                          data-tooltip={getAppointmentTooltip(
                                            apt,
                                          )}
                                          onMouseEnter={(e) => {
                                            const r =
                                              e.currentTarget.getBoundingClientRect();
                                            setTooltip({
                                              text: getAppointmentTooltip(apt),
                                              x: r.left + r.width / 2,
                                              y: r.top,
                                            });
                                          }}
                                          onMouseLeave={() => setTooltip(null)}
                                          draggable={!isVirtual}
                                          onDragStart={
                                            !isVirtual
                                              ? (e) =>
                                                  handleDragStart(
                                                    e,
                                                    apt,
                                                    day,
                                                    hourSlot,
                                                  )
                                              : undefined
                                          }
                                          onDragEnd={handleDragEnd}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAppointmentClick(apt);
                                          }}
                                        >
                                          <AppointmentBadges
  attendanceStatusId={apt.attendanceStatusId}
  hasActionReport={apt.hasActionReport}
  hasTherapistAnswered={apt.hasTherapistAnswered}
  size={12}
  style={{ position: "absolute", top: 2, insetInlineEnd: 2, zIndex: 1, display: "flex", alignItems: "center", gap: 2, }}
  as="div"
/>
                                          <div
                                            className={
                                              styles.weeklyAppointmentPatient
                                            }
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 3,
                                            }}
                                          >
                                            {!apt.patientName &&
                                            apt.treatmentGroupName
                                              ? truncateText(
                                                  apt.treatmentGroupName,
                                                )
                                              : truncateText(
                                                  apt.patientName ||
                                                    apt.comments,
                                                )}
                                          </div>
                                        </div>
                                      );
                                    },
                                  )}
                                  {hasMore && (
                                    <div
                                      className={styles.moreAppointments}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTimeSlot({
                                          date: day.date,
                                          time: hourSlot,
                                          appointments: slotAppointments,
                                        });
                                        setShowTimeSlotModal(true);
                                      }}
                                    >
                                      +{slotAppointments.length - 3}{" "}
                                      {t("APPOINTMENTS_MORE")}
                                    </div>
                                  )}
                                </div>
                              );
                            },
                          );
                        } else {
                          // Default View - One column per day
                          const slotAppointments = getAppointmentsForSlot(
                            day.date,
                            hourSlot,
                          );
                          const displayedAppointments = slotAppointments.slice(
                            0,
                            3,
                          );
                          const hasMore = slotAppointments.length > 3;

                          return (
                            <div
                              key={`${day.date}-all-${hourSlot}`}
                              className={`${getSlotClassName(day.date, hourSlot)} ${isExpanded ? styles.weeklyTimeSlotSub : ""} ${styles.dayDivider}`}
                              onDragOver={(e) =>
                                handleDragOver(e, day.date, hourSlot)
                              }
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, day.date, hourSlot)}
                              onClick={(e) => {
                                if (e.target === e.currentTarget) {
                                  // slot click
                                }
                              }}
                            >
                              <button
                                className={styles.addAppointmentBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCreateModal(day.date, hourSlot);
                                }}
                              >
                                +
                              </button>
                              {displayedAppointments.map(
                                (apt: IAppointment) => {
                                  const fundingColor =
                                    getAppointmentColors(apt);
                                  return (
                                    <div
                                      key={apt.id}
                                      className={styles.weeklyAppointment}
                                      style={{
                                        borderRightColor:
                                          fundingColor.borderColor,
                                        backgroundColor:
                                          fundingColor.backgroundColor,
                                      }}
                                      data-tooltip={getAppointmentTooltip(apt)}
                                      onMouseEnter={(e) => {
                                        const r =
                                          e.currentTarget.getBoundingClientRect();
                                        setTooltip({
                                          text: getAppointmentTooltip(apt),
                                          x: r.left + r.width / 2,
                                          y: r.top,
                                        });
                                      }}
                                      onMouseLeave={() => setTooltip(null)}
                                      draggable
                                      onDragStart={(e) =>
                                        handleDragStart(e, apt, day, hourSlot)
                                      }
                                      onDragEnd={handleDragEnd}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAppointmentClick(apt);
                                      }}
                                    >
                                      <AppointmentBadges
  attendanceStatusId={apt.attendanceStatusId}
  hasActionReport={apt.hasActionReport}
  hasTherapistAnswered={apt.hasTherapistAnswered}
  size={12}
  style={{ position: "absolute", top: 2, insetInlineEnd: 2, zIndex: 1, display: "flex", alignItems: "center", gap: 2, }}
  as="div"
/>
                                      <div
                                        className={
                                          styles.weeklyAppointmentPatient
                                        }
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 3,
                                        }}
                                      >
                                        {!apt.patientName &&
                                        apt.treatmentGroupName
                                          ? truncateText(apt.treatmentGroupName)
                                          : truncateText(
                                              apt.patientName || apt.comments,
                                            )}
                                        {!isColumnSplit &&
                                          getAppointmentOwnerName(apt) &&
                                          (apt.patientName ||
                                            apt.treatmentGroupName) && (
                                            <span
                                              style={{
                                                fontSize: "9px",
                                                opacity: 0.7,
                                                marginRight: "4px",
                                              }}
                                            >
                                              ({getAppointmentOwnerName(apt)})
                                            </span>
                                          )}
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                              {hasMore && (
                                <div
                                  className={styles.moreAppointments}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTimeSlot({
                                      date: day.date,
                                      time: hourSlot,
                                      appointments: slotAppointments,
                                    });
                                    setShowTimeSlotModal(true);
                                  }}
                                >
                                  +{slotAppointments.length - 3}{" "}
                                  {t("APPOINTMENTS_MORE")}
                                </div>
                              )}
                            </div>
                          );
                        }
                      })}

                      {/* Sub-Slots (15, 30, 45) - Only if expanded */}
                      {subSlots.map((subSlot) => (
                        <React.Fragment key={subSlot}>
                          <div
                            className={`${styles.dailyTimeHeader} ${styles.timeHeaderQuarter}`}
                          >
                            {subSlot}
                          </div>
                          {weekDays.map((day) => {
                            if (isColumnSplit) {
                              return displayedTherapists.map(
                                (therapist, tIndex) => {
                                  // Get all appointments for this hour/therapist
                                  const hourlyAppointments =
                                    getAppointmentsForSlot(
                                      day.date,
                                      subSlot,
                                      therapist.id,
                                    );

                                  // Filter for those that belong to this specific sub-slot based on layout
                                  const slotAppointments =
                                    hourlyAppointments.filter((apt) => {
                                      const layout = appointmentLayouts.get(
                                        apt.id,
                                      );
                                      return (
                                        layout &&
                                        layout.containingSlot === subSlot
                                      );
                                    });

                                  const isVirtual = therapist.id === -1;
                                  const slotIsAvailable =
                                    !isVirtual &&
                                    isSlotAvailable(
                                      day.date,
                                      subSlot,
                                      therapist.id,
                                    );

                                  return (
                                    <div
                                      key={`${day.date}-${therapist.id}-${subSlot}`}
                                      className={`${getSlotClassName(day.date, subSlot, therapist.id)} ${styles.weeklyTimeSlotSub} ${
                                        tIndex ===
                                        displayedTherapists.length - 1
                                          ? styles.dayDivider
                                          : ""
                                      }`}
                                      onDragOver={
                                        isVirtual
                                          ? undefined
                                          : (e) =>
                                              handleDragOver(
                                                e,
                                                day.date,
                                                subSlot,
                                                therapist.id,
                                              )
                                      }
                                      onDragLeave={
                                        isVirtual ? undefined : handleDragLeave
                                      }
                                      onDrop={
                                        isVirtual
                                          ? undefined
                                          : (e) =>
                                              handleDrop(
                                                e,
                                                day.date,
                                                subSlot,
                                                therapist.id,
                                              )
                                      }
                                      onClick={(e) => {
                                        if (e.target === e.currentTarget) {
                                          // sub-slot click
                                        }
                                      }}
                                      style={{
                                        position: "relative",
                                        height: "70px",
                                        overflow: "visible",
                                        ...(slotIsAvailable
                                          ? {
                                              backgroundColor:
                                                "rgba(107, 70, 193, 0.1)",
                                            }
                                          : {}),
                                      }}
                                    >
                                      <button
                                        className={styles.addAppointmentBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenCreateModal(
                                            day.date,
                                            subSlot,
                                            therapist.id,
                                          );
                                        }}
                                      >
                                        +
                                      </button>
                                      {slotAppointments.map(
                                        (apt: IAppointment) => {
                                          const fundingColor =
                                            getAppointmentColors(apt);
                                          let appStyles: React.CSSProperties = {
                                            borderRightColor:
                                              fundingColor.borderColor,
                                            backgroundColor:
                                              fundingColor.backgroundColor,
                                          };

                                          const layout = appointmentLayouts.get(
                                            apt.id,
                                          );
                                          if (layout) {
                                            appStyles = {
                                              ...appStyles,
                                              position: "absolute",
                                              top: `${layout.verticalOffset}px`,
                                              height: `${layout.height}px`,
                                              zIndex: 10,
                                            };

                                            if (
                                              layout.position.totalColumns > 1
                                            ) {
                                              const widthPercent =
                                                98 /
                                                layout.position.totalColumns;
                                              const leftPercent =
                                                layout.position.columnIndex *
                                                  widthPercent +
                                                1;
                                              appStyles.width = `calc(${widthPercent}% - 4px)`;
                                              appStyles.left = `calc(${leftPercent}% + 2px)`;
                                              appStyles.right = "auto";
                                            } else {
                                              appStyles.left = "2px";
                                              appStyles.right = "2px";
                                              appStyles.width =
                                                "calc(100% - 4px)";
                                            }
                                          }

                                          return (
                                            <div
                                              key={apt.id}
                                              className={`${styles.weeklyAppointment} ${styles.weeklyAppointmentProportional}`}
                                              style={appStyles}
                                              data-tooltip={getAppointmentTooltip(
                                                apt,
                                              )}
                                              onMouseEnter={(e) => {
                                                const r =
                                                  e.currentTarget.getBoundingClientRect();
                                                setTooltip({
                                                  text: getAppointmentTooltip(
                                                    apt,
                                                  ),
                                                  x: r.left + r.width / 2,
                                                  y: r.top,
                                                });
                                              }}
                                              onMouseLeave={() =>
                                                setTooltip(null)
                                              }
                                              draggable
                                              onDragStart={(e) =>
                                                handleDragStart(
                                                  e,
                                                  apt,
                                                  day,
                                                  subSlot,
                                                )
                                              }
                                              onDragEnd={handleDragEnd}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAppointmentClick(apt);
                                              }}
                                            >
                                              <div
                                                className={
                                                  styles.weeklyAppointmentPatient
                                                }
                                              >
                                                {!apt.patientName &&
                                                apt.treatmentGroupName
                                                  ? truncateText(
                                                      apt.treatmentGroupName,
                                                    )
                                                  : truncateText(
                                                      apt.patientName ||
                                                        apt.comments,
                                                    )}
                                              </div>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  );
                                },
                              );
                            } else {
                              // Default View
                              const slotAppointments = getAppointmentsForSlot(
                                day.date,
                                subSlot,
                              );
                              const displayedAppointments =
                                slotAppointments.slice(0, 3);
                              const hasMore = slotAppointments.length > 3;

                              return (
                                <div
                                  key={`${day.date}-all-${subSlot}`}
                                  className={`${getSlotClassName(day.date, subSlot)} ${styles.weeklyTimeSlotSub} ${styles.dayDivider}`}
                                  onDragOver={(e) =>
                                    handleDragOver(e, day.date, subSlot)
                                  }
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) =>
                                    handleDrop(e, day.date, subSlot)
                                  }
                                  onClick={(e) => {
                                    if (e.target === e.currentTarget) {
                                      // sub-slot default
                                    }
                                  }}
                                  style={{ height: "auto", minHeight: "70px" }}
                                >
                                  <button
                                    className={styles.addAppointmentBtn}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenCreateModal(day.date, subSlot);
                                    }}
                                  >
                                    +
                                  </button>
                                  {displayedAppointments.map(
                                    (apt: IAppointment) => {
                                      const fundingColor =
                                        getAppointmentColors(apt);
                                      return (
                                        <div
                                          key={apt.id}
                                          className={styles.weeklyAppointment}
                                          style={{
                                            borderRightColor:
                                              fundingColor.borderColor,
                                            backgroundColor:
                                              fundingColor.backgroundColor,
                                          }}
                                          data-tooltip={getAppointmentTooltip(
                                            apt,
                                          )}
                                          onMouseEnter={(e) => {
                                            const r =
                                              e.currentTarget.getBoundingClientRect();
                                            setTooltip({
                                              text: getAppointmentTooltip(apt),
                                              x: r.left + r.width / 2,
                                              y: r.top,
                                            });
                                          }}
                                          onMouseLeave={() => setTooltip(null)}
                                          draggable
                                          onDragStart={(e) =>
                                            handleDragStart(
                                              e,
                                              apt,
                                              day,
                                              subSlot,
                                            )
                                          }
                                          onDragEnd={handleDragEnd}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAppointmentClick(apt);
                                          }}
                                        >
                                          <AppointmentBadges
  attendanceStatusId={apt.attendanceStatusId}
  hasActionReport={apt.hasActionReport}
  hasTherapistAnswered={apt.hasTherapistAnswered}
  size={12}
  style={{ position: "absolute", top: 2, insetInlineEnd: 2, zIndex: 1, display: "flex", alignItems: "center", gap: 2, }}
  as="div"
/>
                                          <div
                                            className={
                                              styles.weeklyAppointmentPatient
                                            }
                                          >
                                            {!apt.patientName &&
                                            apt.treatmentGroupName
                                              ? truncateText(
                                                  apt.treatmentGroupName,
                                                )
                                              : truncateText(
                                                  apt.patientName ||
                                                    apt.comments,
                                                )}
                                            {!isColumnSplit &&
                                              getAppointmentOwnerName(apt) &&
                                              (apt.patientName ||
                                                apt.treatmentGroupName) && (
                                                <span
                                                  style={{
                                                    fontSize: "9px",
                                                    opacity: 0.7,
                                                    marginRight: "4px",
                                                  }}
                                                >
                                                  ({getAppointmentOwnerName(apt)})
                                                </span>
                                              )}
                                          </div>
                                        </div>
                                      );
                                    },
                                  )}
                                  {hasMore && (
                                    <div
                                      className={styles.moreAppointments}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTimeSlot({
                                          date: day.date,
                                          time: subSlot,
                                          appointments: slotAppointments,
                                        });
                                        setShowTimeSlotModal(true);
                                      }}
                                    >
                                      +{slotAppointments.length - 3}{" "}
                                      {t("APPOINTMENTS_MORE")}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          })}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Appointment Details Modal */}
      {isModalOpen && selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
          onAppointmentCancelled={handleAppointmentCancelled}
        />
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <CreateAppointmentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          initialDate={createModalDate}
          initialTime={createModalTime}
          therapistId={createModalTherapistId}
        />
      )}

      {/* Move Confirmation Modal */}
      {showMoveConfirmation && pendingMove && (
        <MoveConfirmationModal
          moveData={pendingMove}
          onConfirm={handleConfirmMove}
          onCancel={handleCancelMove}
        />
      )}

      {/* Time Slot Appointments Modal */}
      {showTimeSlotModal &&
        selectedTimeSlot &&
        (() => {
          // Sort appointments by time
          const sortedAppointments = [...selectedTimeSlot.appointments].sort(
            (a, b) => {
              if (!a.time || !b.time) return 0;
              return a.time.localeCompare(b.time);
            },
          );

          // Format date
          const formatDate = (dateStr: string): string => {
            const [year, month, day] = dateStr.split("-").map(Number);
            const dateObj = new Date(year, month - 1, day, 12, 0, 0);
            return dateObj.toLocaleDateString(getLocale(), {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            });
          };

          // Get time range
          const getTimeRange = () => {
            const hour = parseInt(selectedTimeSlot.time.split(":")[0]);
            if (hour === 7) {
              return "00:00 - 07:59";
            } else if (hour === 22) {
              return "22:00 - 23:59";
            } else {
              const nextHour = hour + 1;
              return `${selectedTimeSlot.time} - ${nextHour
                .toString()
                .padStart(2, "0")}:00`;
            }
          };

          return (
            <div
              className={styles.modalOverlay}
              onClick={(e) =>
                e.target === e.currentTarget && setShowTimeSlotModal(false)
              }
            >
              <div className={styles.timeSlotModal}>
                {/* Modal Header */}
                <div className={styles.timeSlotModalHeader}>
                  <div className={styles.timeSlotModalTitle}>
                    <span>🕐</span>
                    <span>{getTimeRange()}</span>
                  </div>
                  <div className={styles.timeSlotDateSubtitle}>
                    {formatDate(selectedTimeSlot.date)}
                  </div>
                  <div className={styles.timeSlotAppointmentsCount}>
                    {t("APPOINTMENTS_TOTAL_APPOINTMENTS", {
                      count: selectedTimeSlot.appointments.length,
                    })}
                  </div>
                  <button
                    className={styles.timeSlotModalCloseBtn}
                    onClick={() => setShowTimeSlotModal(false)}
                  >
                    ×
                  </button>
                </div>

                {/* Modal Body */}
                <div className={styles.timeSlotModalBody}>
                  {sortedAppointments.length === 0 ? (
                    <div className={styles.emptyState}>
                      <span className={styles.emptyIcon}>📭</span>
                      <p>{t("APPOINTMENTS_NO_APPOINTMENTS_FOR_DAY")}</p>
                    </div>
                  ) : (
                    <div className={styles.timeSlotAppointmentsList}>
                      {sortedAppointments.map((apt) => {
                        const fundingColor = getAppointmentColors(apt);
                        return (
                          <div
                            key={apt.id}
                            className={styles.timeSlotAppointmentItem}
                            onClick={() => {
                              setShowTimeSlotModal(false);
                              handleAppointmentClick(apt);
                            }}
                            style={{
                              borderRightColor: fundingColor.borderColor,
                              borderRightWidth: "4px",
                              backgroundColor: fundingColor.backgroundColor,
                            }}
                          >
                            <div className={styles.tsModalAppointmentTime}>
                              <span className={styles.tsModalTimeIcon}>🕐</span>
                              <span className={styles.tsModalTime}>
                                {apt.time}
                              </span>
                              {apt.endTime && (
                                <span className={styles.tsModalEndTime}>
                                  - {apt.endTime}
                                </span>
                              )}
                            </div>

                            <div className={styles.tsModalAppointmentDetails}>
                              {/* Show based on appointment type */}
                              {apt.treatmentGroupName ? (
                                <div className={styles.tsModalAppointmentRow}>
                                  <span className={styles.tsModalIcon}>👥</span>
                                  <span className={styles.tsModalLabel}>
                                    {t("APPOINTMENTS_TREATMENT_GROUP")}:
                                  </span>
                                  <span
                                    className={styles.tsModalValue}
                                    style={{
                                      color: "#10B981",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {apt.treatmentGroupName}
                                  </span>
                                </div>
                              ) : !apt.patientName &&
                                !apt.treatmentGroupName &&
                                !apt.mediaId ? (
                                <div className={styles.tsModalAppointmentRow}>
                                  <span className={styles.tsModalIcon}>📝</span>
                                  <span className={styles.tsModalLabel}>
                                    {t("MESSAGE_CONTENT") || "תוכן ההודעה"}:
                                  </span>
                                  <span className={styles.tsModalValue}>
                                    {apt.comments}
                                  </span>
                                </div>
                              ) : (
                                <div className={styles.tsModalAppointmentRow}>
                                  <span className={styles.tsModalIcon}>👤</span>
                                  <span className={styles.tsModalLabel}>
                                    {t("APPOINTMENTS_PATIENT")}:
                                  </span>
                                  <span className={styles.tsModalValue}>
                                    {apt.patientName}
                                  </span>
                                </div>
                              )}

                              {/* Show therapist row only for regular appointments */}
                              {(apt.patientName ||
                                apt.treatmentGroupName ||
                                apt.mediaId) && (
                                <div className={styles.tsModalAppointmentRow}>
                                  <span className={styles.tsModalIcon}>👨‍⚕️</span>
                                  <span className={styles.tsModalLabel}>
                                    {t("APPOINTMENTS_THERAPIST")}:
                                  </span>
                                  <span className={styles.tsModalValue}>
                                    {getAppointmentOwnerName(apt)}
                                  </span>
                                </div>
                              )}

                              {apt.fundingSourceName && (
                                <div className={styles.tsModalAppointmentRow}>
                                  <span className={styles.tsModalIcon}>💰</span>
                                  <span className={styles.tsModalLabel}>
                                    {t("APPOINTMENTS_FUNDING")}:
                                  </span>
                                  <span
                                    className={styles.tsModalFundingBadge}
                                    style={{
                                      backgroundColor: fundingColor.badgeBg,
                                      color: fundingColor.textColor,
                                    }}
                                  >
                                    {apt.fundingSourceName}
                                  </span>
                                </div>
                              )}

                              {apt.mediaName && (
                                <div className={styles.tsModalAppointmentRow}>
                                  <span className={styles.tsModalIcon}>📍</span>
                                  <span className={styles.tsModalLabel}>
                                    {t("APPOINTMENTS_MEDIA")}:
                                  </span>
                                  <span className={styles.tsModalValue}>
                                    {apt.mediaName}
                                  </span>
                                </div>
                              )}
                            </div>

                            <AppointmentBadges
  attendanceStatusId={apt.attendanceStatusId}
  hasActionReport={apt.hasActionReport}
  hasTherapistAnswered={apt.hasTherapistAnswered}
  size={13}
  style={{ marginInlineStart: "auto", alignSelf: "center", flexShrink: 0, display: "flex", alignItems: "center", gap: 2, }}
  as="div"
/>
                            <div className={styles.tsModalAppointmentArrow}>
                              →
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className={styles.timeSlotModalFooter}>
                  <button
                    className={styles.timeSlotCloseButton}
                    onClick={() => setShowTimeSlotModal(false)}
                  >
                    {t("APPOINTMENTS_CLOSE")}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Success Animation */}
      {showSuccessAnimation && <div className={styles.successAnimation}>✓</div>}

      {/* Toast Notification */}
      {showToast && (
        <div className={styles.dragToast}>
          <div
            className={`${styles.dragToastIcon} ${
              toastType === "success"
                ? styles.dragToastIconSuccess
                : styles.dragToastIconError
            }`}
          >
            {toastType === "success" ? "✓" : "✕"}
          </div>
          <div className={styles.dragToastMessage}>{toastMessage}</div>
        </div>
      )}
      {/* Fixed Tooltip - renders above all sticky headers regardless of stacking context */}
      {tooltip && (
        <div
          className={styles.weeklyTooltip}
          style={{
            top: tooltip.y,
            left: tooltip.x,
            transform: "translateX(-50%) translateY(calc(-100% - 8px))",
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default WeeklyView;
